import React from 'react'
import {
  LuChevronsLeft,
  LuClock,
  LuFileSpreadsheet,
  LuPlus,
} from 'react-icons/lu'
import { useNavigate } from 'react-router'

import ContinueReferralsImportMessage from '@/components/modals/ImportHistoricalReferralsModal/components/ContinueReferralsImportMessage'
import ImportReferralsPrompt from '@/components/modals/ImportHistoricalReferralsModal/components/ImportReferralsPrompt'
import ResolveReferralImports from '@/components/modals/ImportHistoricalReferralsModal/components/ResolveReferralImports'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ImportReferralsProvider } from '@/contexts/ImportReferralsContext'
import { useAddReferralsModal } from '@/hooks/useAddReferralsModal'
import useAppDispatch from '@/hooks/useAppDispatch'
import useAppSelector from '@/hooks/useAppSelector'
import { useImportHistoricalReferralsModal } from '@/hooks/useImportHistoricalReferralsModal'
import useMaxWidth from '@/hooks/useMaxWidth'
import { useUpdateOnboardingChecklistMutation } from '@/hooks/useUpdateOnboardingChecklistMutation'
import { REFERRALS_IMPORT_STEP } from '@/lib/constants'
import { generateUUID } from '@/lib/helpers/helperFunctions'
import { ROUTES } from '@/lib/routes'
import { selectOnboardingChecklist } from '@/redux/slices/onboardingChecklist'
import { initialReferralsImportItemState } from '@/redux/slices/referralsImportBase'
import {
  selectReferralsImportHistorical,
  setImportItems,
  setIsOpen,
  setStep,
} from '@/redux/slices/referralsImportHistorical'
import { BulkImportReferralCollection } from '@/types/responses/referral-import'

const ImportHistoricalReferralsModal: React.FC = () => {
  const isMaxWidthMd = useMaxWidth('md')
  const addReferralsModal = useAddReferralsModal()
  const importHistoricalReferralsModal = useImportHistoricalReferralsModal()
  const navigate = useNavigate()
  const referralsImportHistoricalState = useAppSelector(
    selectReferralsImportHistorical,
  )
  const updateChecklistMutation = useUpdateOnboardingChecklistMutation()
  const checklist = useAppSelector(selectOnboardingChecklist)
  const dispatch = useAppDispatch()
  const fullscreen =
    isMaxWidthMd ||
    referralsImportHistoricalState.step === REFERRALS_IMPORT_STEP.resolveImport

  function handleSuccessfulUpload(data: BulkImportReferralCollection) {
    dispatch(
      setImportItems(
        data['hydra:member'].map((item) => ({
          ...initialReferralsImportItemState,
          ...item,
          refUUID: generateUUID(),
          onClientDuplicate: referralsImportHistoricalState.onClientDuplicate,
        })),
      ),
    )
    dispatch(setStep(REFERRALS_IMPORT_STEP.resolveImport))
  }

  function handleStartOver() {
    dispatch(setStep(referralsImportHistoricalState.initialStep))
    dispatch(setImportItems([]))
  }

  function handleFinish() {
    dispatch(setStep(referralsImportHistoricalState.initialStep))
    dispatch(setImportItems([]))
    dispatch(setIsOpen(false))

    markCompletedInOnboardingChecklist()
  }

  function handleAutomaticFinish() {
    dispatch(setStep(referralsImportHistoricalState.initialStep))
    dispatch(setImportItems([]))
    dispatch(setIsOpen(false))

    navigate(ROUTES.referrals())

    markCompletedInOnboardingChecklist()
  }

  function markCompletedInOnboardingChecklist() {
    // Don't bother doing anything if it's already complete
    if (checklist?.uploadHistoricalReferrals === false) {
      updateChecklistMutation.mutate({
        checklistId: checklist?.id,
        request: { uploadHistoricalReferrals: true },
      })
    }
  }

  function handleClose() {
    dispatch(setIsOpen(false))
  }

  const StartOverButton = (
    <Button
      variant="outline-secondary"
      size="sm"
      onClick={handleStartOver}
      className="w-full sm:w-auto"
    >
      <LuPlus />
      Import more
    </Button>
  )

  const step = referralsImportHistoricalState.step

  if (step === REFERRALS_IMPORT_STEP.upload) {
    return (
      <Dialog
        open={importHistoricalReferralsModal.isShowing}
        onHide={handleClose}
      >
        <DialogContent
          size={fullscreen ? 'max' : 'md'}
          className="bg-background text-muted-foreground p-6"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div
                className="size-10 rounded-full bg-primary/10 flex items-center
                  justify-center"
              >
                <LuClock className="text-primary text-xl" />
              </div>
              Import from spreadsheet
            </DialogTitle>
          </DialogHeader>

          <DialogBody className="max-h-[55vh] px-0 mx-0">
            <p className="mb-5">
              Drag or upload your existing spreadsheet. Our smart importer will
              turn messy historical data into instant insights on referrals,
              partners, and revenue.
            </p>

            {referralsImportHistoricalState.importItems.length > 0 && (
              <ContinueReferralsImportMessage />
            )}
            <ImportReferralsProvider>
              <ImportReferralsPrompt
                onSuccessfulUpload={handleSuccessfulUpload}
                className="mt-4"
              />
            </ImportReferralsProvider>
          </DialogBody>

          <DialogFooter>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                handleClose()
                addReferralsModal.showModal()
              }}
            >
              Back
            </Button>

            <div className="grow"></div>

            <Button onClick={handleFinish} disabled size="sm">
              Import referrals
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  if (step === REFERRALS_IMPORT_STEP.resolveImport) {
    return (
      <Dialog
        open={importHistoricalReferralsModal.isShowing}
        onHide={handleClose}
      >
        <DialogContent size={fullscreen ? 'max' : 'md'}>
          <DialogHeader>
            <DialogTitle>
              <Button
                variant="ghost"
                className="text-muted-foreground font-normal mb-2"
                onClick={() => dispatch(setStep(REFERRALS_IMPORT_STEP.upload))}
              >
                <LuChevronsLeft className="text-xl" />
                Back
              </Button>

              <div className="flex items-center gap-2">
                <div
                  className="size-10 rounded-xl bg-primary/10 flex items-center
                    justify-center"
                >
                  <LuFileSpreadsheet className="text-primary text-xl" />
                </div>
                Import Preview
              </div>
            </DialogTitle>
          </DialogHeader>
          {/*ResolveReferralImports renders DialogBody and DialogFooter elements */}
          <ResolveReferralImports
            startOverButton={StartOverButton}
            handleFinish={handleFinish}
            handleAutomaticFinish={handleAutomaticFinish}
          />
        </DialogContent>
      </Dialog>
    )
  }

  throw `Invalid import step ${referralsImportHistoricalState.step}`
}

export default ImportHistoricalReferralsModal
